import { useGoBackEvent } from "app/hooks/go-back.hook";
import { useFavorites } from "app/hooks/favorites.hook";
import { useArrowKeyNavigationOverlay } from "app/hooks/keyboard-navigation.hook";
import { selectUserState } from "app/store/slices/user";
import { useAppDispatch, useAppSelector } from "app/store/store";
import clsx from "clsx";
import { RequestError } from "services-hooks/config";
import {
  usePostPillowDeezerV2DeezerTrack_addRadio_id,
  usePostPillowSpotifyV2SpotifyTrack_addRadio_id,
} from "services-hooks/hooks";
import { Category, RadioLiveMetadata } from "services-hooks/types";
import MemoAddToPlaylist from "../icons/AddToPlaylist";
import MemoAngleDown from "../icons/AngleDown";
import MemoHeartFull from "../icons/HeartFull";
import MemoHeartOutline from "../icons/HeartOutline";
import MemoInfo from "../icons/Info";
import Loader from "../icons/Loader";
import MemoPause from "../icons/Pause";
import MemoPlay from "../icons/Play";
import MemoSpotifyAdd from "../icons/SpotifyAdd";
import { useToast, useToasts } from "../toast/toast.component";
import useModalScroll from "app/hooks/modal-scroll.hook";
import styles from "./player-fullscreen.module.scss";
import { CSSTransition } from "react-transition-group";
import { selectPlayerState, playerActions } from "app/store/slices/player";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useClickAway,
  useInterval,
  useNetworkState,
  useWindowSize,
} from "react-use";
import { StationsRow } from "../stations-list/stations-row.component";
import MemoDeezerIcon from "../icons/DeezerIcon";
import MemoLoader from "../icons/Loader";
import MemoPlaylist from "../icons/Playlist";
import { HTMLMediaControls, HTMLMediaState } from "app/hooks/audio.hook";
import { useTranslate } from "app/hooks/lang";
import { useNavigatePath } from "app/hooks/navigation.hook";
import { usePlayerContext, VLYBY_AD_ERROR_TIMEOUT, VLYBY_TIMEOUT } from "app/context/player.context";
import { useFeaturesContext } from "app/context/features.context";
import { Button } from "../ui/button.component";
import Link from "next/link";
import { timeout } from "app/ts";
import { VlybyContainer } from "./vlyby-container.component";
import { useCountryContext } from "app/context/country.context";

interface PlayerFullScreenProps {
  playerCssVariables: object;
  category: Category;
  cover: string;
  categoryMetadata: RadioLiveMetadata | undefined;
  onClose: VoidFunction;
  goToDetails: VoidFunction;
  open: boolean;
  playbackState: HTMLMediaState;
  playbackControls: HTMLMediaControls;
  backToHTTPS?: (mode?: "close" | "next" | "prev") => void;
}

export const PlayerFullScreen: React.FC<PlayerFullScreenProps> = ({
  playerCssVariables,
  category,
  onClose,
  playbackState,
  playbackControls,
  categoryMetadata,
  goToDetails,
  cover,
  open,
  backToHTTPS
}) => {
  const {
    audioDotsEnabled,
    audioDotsPublisherId,
    audioDotsChannelId,
    premiumVersion,
    vlybyPublisherId,
  } = useFeaturesContext();
  const {
    audioDots,
    checkGlobalTimer,
    setGlobalAdCloseTime,
    globalAdCloseTime,
    httpMode,
    priceData
  } = usePlayerContext();
  const navigatePath = useNavigatePath();
  const t = useTranslate();
  const {
    toggleFavorite,
    loading: favoritesLoading,
    isFavoriteCategory,
  } = useFavorites(() => {
    onClose();
    navigatePath("/log-in", true, { openplayer: true });
  });
  const [videoAdActive, setVideoAdActive] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useModalScroll(open);

  const { mutateAsync: addToSpotify, isLoading: spotifyLoading } =
    usePostPillowSpotifyV2SpotifyTrack_addRadio_id();
  const { mutateAsync: addToDeezer, isLoading: deezerLoading } =
    usePostPillowDeezerV2DeezerTrack_addRadio_id();
  const { width, height } = useWindowSize();
  const controlsContainerRef = useRef<HTMLDivElement>(null);
  const { session } = useAppSelector(selectUserState);
  const [showSpotifySuccess, showSpotifyError] = useToasts({
    success: t("playlsit.spotify-success"),
    error: t("playlsit.spotify-error"),
  });
  const [showDeezerSuccess, showDeezerError] = useToasts({
    success: t("playlsit.deezer-success"),
    error: t("playlsit.deezer-error"),
  });
  const httpModeToast = useToast({content: "Updating the player..."});
  
  const isAddToPlaylistLoading = spotifyLoading || deezerLoading;
  const goBackCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  useGoBackEvent("PlayerFullScreen", goBackCallback, open && !sidebarOpen);

  const addToPlaylist = async () => {
    if (session?.user?.spotify_connect) {
      try {
        const response = await addToSpotify({
          radio_id: category.permalink!,
        });
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.message
        ) {
          showSpotifySuccess(response.data.data.message);
        } else {
          showSpotifySuccess();
        }
      } catch (e) {
        if (e instanceof RequestError) {
          const error = e.response?.data.errors[0];
          showSpotifyError(error.message);
        }
      }
    } else if (session?.user?.deezer_connect) {
      try {
        const response = await addToDeezer({
          radio_id: category.permalink!,
        });
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.message
        ) {
          showDeezerSuccess(response.data.data.message);
        } else {
          showDeezerError();
        }
      } catch (e) {
        if (e instanceof RequestError) {
          const error = e.response?.data.errors[0];
          showSpotifyError(error.message);
        }
      }
    } else if (!session?.user?.premium) {
      navigatePath("/get-premium", true, {
        redirect: "/settings?accountlink=true",
      });
      onClose();
    } else {
      navigatePath("/settings");
      onClose();
    }
  };

  const wrapperRef = useArrowKeyNavigationOverlay(open);
  const renderAddToPlaylistIcon = () => {
    if (session?.user) {
      if (session.user.spotify_connect) {
        return <MemoSpotifyAdd />;
      } else if (session.user.deezer_connect) {
        return <MemoDeezerIcon />;
      }
    }
    return <MemoAddToPlaylist />;
  };
  const [audioDotPlayer, setAudioDotPlayer] = useState<any | null>(null);
  // const tryVidoeAdsPlayback = () => {
  //   if (audioDots.playerConstructor) {
  //     const { desiredHeight, desiredWidth } = getAdContainerMaximumSize();
  //     const adConfig = {
  //       publisherId: audioDotsPublisherId,
  //       channelId: audioDotsChannelId,
  //       ref1: "",
  //       height: desiredHeight,
  //       width: desiredWidth,
  //       autoPlay: true,
  //       loop: true,
  //       vastRetry: 3,
  //       errorLimit: 5,
  //       soundButton: false,
  //       pauseButton: false,
  //       midRollTime: 2,
  //       position: "ad_player_radio",
  //     };
  //     const player = new audioDots.playerConstructor(adConfig);

  //     setAudioDotPlayer(player);
  //     player.on("AdImpression", (e: any) => {
  //       setVideoAdActive(true);
  //     });
  //     player.on("AdVideoComplete", () => {
  //       setVideoAdActive(false);
  //       player.close();
  //       stopVideoAds();
  //     });
  //     player.play(adConfig);
  //   }
  // };

  const stopVideoAds = async () => {
    setVideoAdActive(false);
    await audioDotPlayer?.close();
    setAudioDotPlayer(null);
  };
  const videoAdsEnabled =
    audioDotsEnabled &&
    !!audioDots &&
    !premiumVersion &&
    !session?.user?.premium &&
    open;
  // useInterval(
  //   async () => {
  //     console.log("++++interval", new Date());

  //     if (!videoAdActive) {
  //       if (audioDotPlayer) {
  //         stopVideoAds();
  //         await timeout(2000);
  //       }
  //       tryVidoeAdsPlayback();
  //     }
  //   },
  //   videoAdsEnabled ? 5 * 60000 : null
  // );

  // VLYBY
  const networkState = useNetworkState();
  const enableVlyby = !!vlybyPublisherId && !premiumVersion;
  const { country } = useCountryContext();
  const [isVlybyAdReady, setIsVlybyAdReady] = useState(false);
  const [isFirstVlybyAdPlay, setIsFirstVlybyAdPlay] = useState(true);
  const getIsPageVisible = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.document.visibilityState === "visible";
    }
    return true;
  }, [window]);
  const [isAdblockDetected, setIsAdblockDetected] = useState(false);
  const hideAdTimeoutId = useRef<any>(null);
  const showAdTimeoutId = useRef<any>(null);
  const adFreeTime = 3 * 60000; //3min
  const firstAudioAdTimeout = 30000; //30s

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        (window as any).innerHideVlybyPlayer = function () {

          stopVlybyVideoAds((window as any).isVlybyAdReady);
        };
        (window as any).innerVlybyAdReady = function () {

          (window as any).isVlybyAdReady = true;
          setIsVlybyAdReady(true);
        };
      }
    } catch (error) {}

    return () => {
      try {
        if (typeof window !== "undefined") {
          delete (window as any).innerHideVlybyPlayer;
          delete (window as any).innerVlybyAdReady;
          delete (window as any).isVlybyAdReady;
        }
      } catch (error) {}
    };
  }, []);

  const stopVlybyVideoAds = async (
    isSuccess = false,
    stopNextTimeout = false,
    isAdError = false
  ) => {
    clearTimeout(hideAdTimeoutId.current);
    clearTimeout(showAdTimeoutId.current);

    (window as any).isVlybyAdReady = false;
    setIsVlybyAdReady(false);
    setVideoAdActive(false);
    setShowVlyby(false);

    if (isSuccess) {
      setGlobalAdCloseTime(Number(Date.now()));
    }
    if (enableVlyby && !stopNextTimeout) {
      showAdTimeoutId.current = setTimeout(() => {
        startLoadingVlyby();
      }, isAdError ? VLYBY_AD_ERROR_TIMEOUT : adFreeTime);
    }
  };

  // test adblock
  useEffect(() => {
    if (typeof window !== "undefined") {
      const adBoxEl = window.document.querySelector(".ad-box");
      setIsAdblockDetected(
        !!adBoxEl && window.getComputedStyle(adBoxEl)?.display === "none"
      );
    }
  }, []);

  const [showVlyby, setShowVlyby] = useState(false);

  const startLoadingVlyby = () => {
    if (!videoAdActive) {
      clearTimeout(hideAdTimeoutId.current);

      const localCheck =
        enableVlyby &&
        country.countryCode !== "de" &&
        getIsPageVisible() &&
        !session?.user?.premium &&
        !isAdblockDetected;

      console.log("[[VLYBY]] START LOADING", localCheck, {
        open,
        isFirstVlybyAdPlay,
        globalAdCloseTime,
      });
      setShowVlyby(localCheck);
      setVideoAdActive(localCheck);

      if (localCheck) {
        clearTimeout(hideAdTimeoutId.current);
        hideAdTimeoutId.current = setTimeout(() => {
          if (typeof window !== "undefined") {
            console.log(
              "[[VLYBY]] timeout",
              hideTimeout,
              isVlybyAdReady,
              (window as any).isVlybyAdReady,
              showVlyby,
              localCheck
            );
            if (!(window as any).isVlybyAdReady) {
              stopVlybyVideoAds(false, false, true);
            }
          }
        }, hideTimeout);
      } else {
        stopVlybyVideoAds();
      }
    }
  };

  const hideTimeout = useMemo(() => {
    if (networkState.effectiveType) {
      if (["2g", "slow-2g"].includes(networkState.effectiveType)) {
        return 30000;
      }
      if (["3g"].includes(networkState.effectiveType)) {
        return 15000;
      }
    }
    return 10000;
  }, [networkState]);

  // start loading vlyby
  useEffect(() => {
    clearTimeout(hideAdTimeoutId.current);
    clearTimeout(showAdTimeoutId.current);

    if (enableVlyby && open && isFirstVlybyAdPlay) {
      // first ad after 30s
      showAdTimeoutId.current = setTimeout(
        () => {
          setIsFirstVlybyAdPlay(false);
          startLoadingVlyby();
        },
        checkGlobalTimer()
          ? firstAudioAdTimeout
          : VLYBY_TIMEOUT - (Number(Date.now()) - globalAdCloseTime) + 10
      );
    } else if (enableVlyby && !open) {
      (window as any).isVlybyAdReady = false;
      setIsVlybyAdReady(false);
      setVideoAdActive(false);
      setShowVlyby(false);
      setIsFirstVlybyAdPlay(true);
    }
  }, [open]);

  const showTopRightArtWork =
    (categoryMetadata?.body?.content?.show?.image ||
      categoryMetadata?.body?.content?.track?.album?.cover) &&
    category.logo;

  useEffect(() => {
    if (!videoAdsEnabled) {
      stopVideoAds();
    }
  }, [videoAdsEnabled]);

  const getAdContainerMaximumSize = (): {
    desiredHeight: number;
    desiredWidth: number;
  } => {
    const maximumHeight =
      window.innerHeight -
      252 -
      (controlsContainerRef.current?.clientHeight || 0);
    const proportionalMaximumWidth = maximumHeight * (16 / 9);
    const maximumWidth =
      proportionalMaximumWidth > 960 ? 960 : proportionalMaximumWidth;
    const desiredWidth =
      window.innerWidth - 80 > maximumWidth
        ? maximumWidth
        : window.innerWidth - 80;
    const desiredHeight = desiredWidth * (9 / 16);
    return { desiredHeight, desiredWidth };
  };

  const [adPlayerWidth, setAdPlayerWidth] = useState("");
  const [adPlayerHeight, setAdPlayerHeight] = useState("");

  useEffect(() => {
    if (audioDotPlayer || enableVlyby) {
      const { desiredHeight, desiredWidth } = getAdContainerMaximumSize();
      // if (audioDotPlayer) {
      //   audioDotPlayer.resize(desiredWidth, desiredHeight);
      // }
      if (enableVlyby) {
        setAdPlayerWidth(`${desiredWidth}px`);
        setAdPlayerHeight(`${desiredHeight}px`);
      }
    }
  }, [width, height]);

  const closeSidebarCallback = useCallback(
    () => setSidebarOpen(false),
    [setSidebarOpen]
  );

  return (
    <CSSTransition
      nodeRef={wrapperRef}
      in={open}
      timeout={httpMode ? 0 : 400}
      classNames={{
        enterActive: styles.player_fullscreen_enter_active,
        enter: styles.player_fullscreen_enter,
        enterDone: styles.player_fullscreen_enter_done,
        exit: styles.player_fullscreen_exit,
        exitActive: styles.player_fullscreen_exit_active,
        exitDone: styles.player_fullscreen_exit_done,
      }}
    >
      <div className={styles.player_fullscreen} ref={wrapperRef}>
        <div className={styles.player_fullscreen_wrapper}>
          <div
            className={styles.player_fullscreen_content}
            style={playerCssVariables}
          >
            <div className={styles.player_fullscreen_inner}>
              <div
                className={styles.player_fullscreen_header}
                data-navigate-row={open}
              >
                <button
                  onClick={() => {
                    if (httpMode && backToHTTPS) {
                      backToHTTPS("close");
                    } else {
                      onClose();
                    }
                  }}
                  className={styles.player_fullscreen_close}
                  data-navigate-item={open}
                  data-navigate-noscroll
                >
                  <MemoAngleDown />
                </button>
                {(videoAdsEnabled || showVlyby || showTopRightArtWork) && (
                  <img
                    className={clsx(styles.player_logo, {
                      [styles.ad_visible]:
                        videoAdActive && (window as any).isVlybyAdReady,
                    })}
                    src={category.logo}
                    alt=""
                  />
                )}
              </div>
              <div
                className={clsx(styles.player_fullscreen_track, {
                  [styles.video_ad]: videoAdsEnabled || showVlyby,
                  [styles.invisible]: !videoAdActive && !showVlyby,
                  [styles.video_ad_ready]: (window as any).isVlybyAdReady,
                })}
              >
                {(window as any).isVlybyAdReady && (
                  <>
                    <div className={styles.video_ad_header}>
                      <Link href={"/get-premium"} passHref>
                        <a data-navigate-row={videoAdActive || showVlyby}>
                          <Button
                            accent
                            data-navigate-item={videoAdActive || showVlyby}
                            data-navigate-noscroll
                            className={styles.premium_button}
                            onClick={() => onClose()}
                          >
                            {t("premium.go-premium-for", { var1: `${priceData.amount}${priceData.currency_symbol}` })}
                          </Button>
                        </a>
                      </Link>
                    </div>
                    <p className={styles.ad_header}>
                      {t("premium.video-ad-description")}
                    </p>
                  </>
                )}

                <div
                  className={styles.ad_player}
                  style={{
                    display: !audioDotPlayer && !showVlyby ? "none" : undefined,
                    margin: "0px auto",
                    maxWidth: "853px",
                  }}
                >
                  {showVlyby ? (
                    <div className={styles.vlyby_container}>
                      <VlybyContainer
                        width={adPlayerWidth}
                        height={adPlayerHeight}
                      />
                    </div>
                  ) : audioDotsEnabled && videoAdActive ? (
                    <div id="ad_player_radio"></div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <div
                className={clsx(styles.player_fullscreen_track, {
                  // [styles.invisible]: videoAdActive || showVlyby,
                  // [styles.video_ad_active]:
                  //   videoAdActive ||
                  //   (showVlyby && ((window as any).isVlybyAdReady)),
                  [styles.invisible]: (window as any).isVlybyAdReady,
                })}
              >
                <img src={cover} alt="" />
                <div className={styles.player_fullscreen_track_info}>
                  <h2 className={styles.title}>{category.name}</h2>
                  {categoryMetadata?.body?.content?.show?.name && (
                    <p className={clsx(styles.show_name)}>
                      {categoryMetadata?.body?.content?.show?.name}
                    </p>
                  )}
                  {categoryMetadata?.body?.content?.track?.name && (
                    <p className={clsx(styles.track_name)}>
                      {categoryMetadata?.body?.content?.track?.name}
                    </p>
                  )}
                  {categoryMetadata?.body?.content?.track?.artist?.name && (
                    <p className={clsx(styles.track_artist)}>
                      {categoryMetadata?.body?.content?.track?.artist?.name}
                    </p>
                  )}
                </div>
                <div
                  className={clsx(
                    styles.player_fullscreen_track_info,
                    styles.player_fullscreen_track_info_ads
                  )}
                >
                  <p className={clsx(styles.show_name)}>
                    {categoryMetadata?.body?.content?.show?.name ||
                      category.name}
                  </p>
                </div>
              </div>
              <div
                className={styles.player_fullscreen_controls}
                data-navigate-row={open}
                ref={controlsContainerRef}
              >
                <button
                  data-navigate-item={open}
                  className={styles.button_secondary}
                  data-navigate-noscroll
                  onClick={goToDetails}
                >
                  <MemoInfo />
                </button>
                <button
                  data-navigate-item={open}
                  data-navigate-noscroll
                  className={clsx(styles.button_priamry, styles.heart)}
                  onClick={() =>
                    toggleFavorite(category, playbackState.playing)
                  }
                >
                  {favoritesLoading ? (
                    <Loader />
                  ) : isFavoriteCategory(category) ? (
                    <MemoHeartFull />
                  ) : (
                    <MemoHeartOutline />
                  )}
                </button>
                <button
                  data-navigate-item={open}
                  data-navigate-noscroll
                  className={clsx(styles.play, {
                    [styles.show_play]: !playbackState.playing,
                  })}
                  onClick={() => {
                    if (!playbackState.loading) {
                      playbackControls.toggle();
                    }
                  }}
                >
                  {playbackState.loading ? (
                    <MemoLoader />
                  ) : (
                    <>{playbackState.playing ? <MemoPause /> : <MemoPlay />}</>
                  )}
                </button>
                <button
                  className={clsx(
                    styles.button_priamry,
                    styles.add_to_playlist_icon
                  )}
                  onClick={() => addToPlaylist()}
                  data-navigate-noscroll
                  data-navigate-item={open}
                >
                  {isAddToPlaylistLoading ? (
                    <Loader />
                  ) : (
                    renderAddToPlaylistIcon()
                  )}
                </button>
                <button
                  className={clsx(
                    styles.button_secondary,
                    styles.playlist_icon
                  )}
                  data-navigate-item={open}
                  data-navigate-noscroll
                  onClick={() => setSidebarOpen(true)}
                >
                  <MemoPlaylist />
                </button>
              </div>
            </div>
          </div>
        </div>

        <PlayerSidebar open={sidebarOpen} onClose={closeSidebarCallback} />
      </div>
    </CSSTransition>
  );
};
interface PlayerSidebarProps {
  open: boolean;
  onClose: VoidFunction;
}
const PlayerSidebar: React.FC<PlayerSidebarProps> = ({ open, onClose }) => {
  const { radioQueue } = useAppSelector(selectPlayerState);
  const dispatch = useAppDispatch();
  const sidebarRef = useArrowKeyNavigationOverlay(open);
  useClickAway(sidebarRef, (e) => {
    onClose();
  });
  const goBackCallback = useCallback(() => {
    onClose();
  }, [onClose]);
  useGoBackEvent("PlayerSidebar", goBackCallback, open);
  return (
    <CSSTransition
      nodeRef={sidebarRef}
      in={open}
      timeout={400}
      classNames={{
        enterActive: styles.player_sidebar_enter_active,
        enter: styles.player_sidebar_enter,
        enterDone: styles.player_sidebar_enter_done,
        exit: styles.player_sidebar_exit,
        exitActive: styles.player_sidebar_exit_active,
        exitDone: styles.player_sidebar_exit_done,
      }}
    >
      <div className={styles.player_sidebar} ref={sidebarRef}>
        <div className={styles.player_sidebar_inner}>
          <StationsRow
            column
            handleOnClick={(_, index) => {
              dispatch(
                playerActions.setRadioQueue({
                  items: radioQueue.items,
                  activeIndex: index,
                })
              );
              onClose();
            }}
            items={radioQueue.items}
            showStationNames
            navigationDisabled={!open}
          />
        </div>
      </div>
    </CSSTransition>
  );
};
